/* MolliePaymentsPanel — iter913
 *
 * Admin tab replacing WisePaymentsPanel. Shows pending + paid Mollie
 * transactions from `mollie_pending_payments`, allows manual `confirm`
 * (when a webhook is missed), and lets admins delete abandoned rows.
 *
 * Unlike Wise, there's no webhook self-registration UI — Mollie's
 * webhook URL is provided per-payment when we call `payments.create`,
 * so nothing needs to be configured in the dashboard.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  Trash2,
  RefreshCw,
  Wallet,
  Activity,
  AlertTriangle,
  HelpCircle,
  Radio,
  Send,
} from "lucide-react";

const STATUS_META = {
  paid:     { label: "Платено",   Icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  pending:  { label: "Изчаква",   Icon: Clock,        cls: "text-amber-700 bg-amber-50 border-amber-200" },
  open:     { label: "Отворено",  Icon: Clock,        cls: "text-blue-700 bg-blue-50 border-blue-200" },
  canceled: { label: "Отменено",  Icon: XCircle,      cls: "text-slate-600 bg-slate-100 border-slate-200" },
  expired:  { label: "Изтекло",   Icon: XCircle,      cls: "text-slate-600 bg-slate-100 border-slate-200" },
  failed:   { label: "Неуспешно", Icon: XCircle,      cls: "text-red-700 bg-red-50 border-red-200" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border ${meta.cls}`}
      data-testid={`mollie-status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function fmtEur(v) {
  if (v == null) return "—";
  return `${Number(v).toFixed(2)} €`;
}

export default function MolliePaymentsPanel({ API, isDark }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyTx, setBusyTx] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("nutramilo_access_token") ||
    "";
  const auth = { Authorization: `Bearer ${token}` };

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/mollie/health`, { headers: auth });
      setHealth(data);
    } catch (e) {
      /* silent */
    } finally {
      setHealthLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/mollie/pending?limit=200`,
        { headers: auth }
      );
      setRows(data.payments || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при зареждане");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  useEffect(() => {
    load();
    loadHealth();
    // Auto-refresh health every 30 s so the dashboard is "live" during
    // the first-24h go-live window.
    const iv = setInterval(loadHealth, 30_000);
    return () => clearInterval(iv);
  }, [load, loadHealth]);

  const runTestWebhook = async () => {
    if (testing) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/mollie/test-webhook`,
        {},
        { headers: auth }
      );
      setTestResult(data);
      if (data.reachable) {
        toast.success("Webhook достижим — HTTP " + data.response_status);
      } else {
        toast.error("Webhook НЕ е достижим: " + (data.error || `HTTP ${data.response_status}`));
      }
      // Refresh health so the new event shows up in the timeline.
      setTimeout(loadHealth, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test webhook грешка");
    } finally {
      setTesting(false);
    }
  };

  const confirmTx = async (tx_id, amount, currency) => {
    if (!window.confirm(`Ръчно активиране на ${tx_id}?`)) return;
    setBusyTx(tx_id);
    try {
      await axios.post(
        `${API}/admin/mollie/${tx_id}/confirm`,
        { amount: Number(amount || 0), currency: currency || "EUR", note: "manual" },
        { headers: auth }
      );
      toast.success(`${tx_id} активирано`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при активиране");
    } finally {
      setBusyTx(null);
    }
  };

  const deleteTx = async (tx_id) => {
    if (!window.confirm(`Изтрий ${tx_id}?`)) return;
    setBusyTx(tx_id);
    try {
      await axios.delete(`${API}/admin/mollie/${tx_id}`, { headers: auth });
      toast.success(`${tx_id} изтрито`);
      setRows((prev) => prev.filter((r) => r.tx_id !== tx_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при изтриване");
    } finally {
      setBusyTx(null);
    }
  };

  const paidCount = rows.filter((r) => r.status === "paid").length;
  const totalPaid = rows
    .filter((r) => r.status === "paid")
    .reduce((s, r) => s + Number(r.paid_amount || r.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="mollie-admin-container">
      {/* ── iter915: Webhook health widget ─────────────────────────── */}
      <MollieHealthWidget
        health={health}
        loading={healthLoading}
        onRefresh={loadHealth}
        onTest={runTestWebhook}
        testing={testing}
        testResult={testResult}
        isDark={isDark}
      />

      <div data-testid="mollie-payments-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            Mollie плащания
          </h2>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {rows.length} транзакции · {paidCount} платени · общо {fmtEur(totalPaid)}
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          data-testid="mollie-panel-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Обнови
        </Button>
      </div>

      <div
        className={`rounded-xl border overflow-hidden ${
          isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"
        }`}
      >
        <table className="w-full text-sm">
          <thead className={isDark ? "bg-slate-900 text-slate-400" : "bg-slate-50 text-slate-600"}>
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold">TX / Дата</th>
              <th className="px-3 py-2 font-semibold">Потребител</th>
              <th className="px-3 py-2 font-semibold">План</th>
              <th className="px-3 py-2 font-semibold text-right">Сума</th>
              <th className="px-3 py-2 font-semibold">Статус</th>
              <th className="px-3 py-2 font-semibold text-right">Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Зареждане…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-500">
                  <Wallet className="w-8 h-8 opacity-40 mx-auto mb-2" />
                  Няма транзакции още.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.tx_id}
                data-testid={`mollie-row-${r.tx_id}`}
                className={`border-t ${
                  isDark ? "border-slate-800 hover:bg-slate-900" : "border-slate-100 hover:bg-slate-50"
                }`}
              >
                <td className="px-3 py-2 font-mono text-[12px] align-top">
                  <div className="font-semibold">{r.tx_id}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {r.created_at?.slice(0, 16).replace("T", " ")}
                  </div>
                  {r.mollie_payment_id && (
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {r.mollie_payment_id}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="text-[12px]">{r.user_email || "—"}</div>
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="text-[12px]">{r.plan_label || r.plan_id}</div>
                  <div className="text-[10px] text-slate-500">
                    {r.billing_period}
                    {r.region ? ` · ${r.region}` : ""}
                  </div>
                </td>
                <td className="px-3 py-2 text-right align-top font-semibold">
                  {fmtEur(r.paid_amount || r.amount)}
                  <div className="text-[10px] text-slate-500 font-normal">
                    {r.currency || "EUR"}
                  </div>
                </td>
                <td className="px-3 py-2 align-top">
                  <StatusBadge status={r.status === "paid" ? "paid" : (r.mollie_status || r.status)} />
                </td>
                <td className="px-3 py-2 text-right align-top">
                  <div className="inline-flex gap-1">
                    {r.status !== "paid" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyTx === r.tx_id}
                        onClick={() => confirmTx(r.tx_id, r.amount, r.currency)}
                        data-testid={`mollie-confirm-${r.tx_id}`}
                      >
                        {busyTx === r.tx_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-3 h-3" />
                        )}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyTx === r.tx_id}
                      onClick={() => deleteTx(r.tx_id)}
                      data-testid={`mollie-delete-${r.tx_id}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

/* ── iter915: Webhook health widget ──────────────────────────────────
 * Traffic-light indicator, key timestamps, recent-event timeline, and a
 * "Test webhook" button that POSTs a real Mollie payment_id to our own
 * webhook URL from outside the pod — proves end-to-end reachability
 * before the first paying customer arrives.
 */
const REACH_META = {
  green:   { label: "Достижим",              cls: "text-emerald-700 bg-emerald-50 border-emerald-300", dot: "bg-emerald-500", Icon: Radio },
  amber:   { label: "1–24ч без събитие",     cls: "text-amber-800 bg-amber-50 border-amber-300",       dot: "bg-amber-500",   Icon: AlertTriangle },
  red:     { label: "НЕДОСТИЖИМ (>24ч)",     cls: "text-red-800 bg-red-50 border-red-300",             dot: "bg-red-500 animate-pulse", Icon: XCircle },
  unknown: { label: "Няма данни още",        cls: "text-slate-700 bg-slate-50 border-slate-300",       dot: "bg-slate-400",   Icon: HelpCircle },
};

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = (now - d) / 60_000;
    if (diffMin < 1) return "преди <1 мин";
    if (diffMin < 60) return `преди ${Math.round(diffMin)} мин`;
    if (diffMin < 24 * 60) return `преди ${Math.round(diffMin / 60)} ч`;
    return d.toLocaleString("bg-BG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

function EventStatusBadge({ status }) {
  const clsMap = {
    paid:     "bg-emerald-100 text-emerald-800",
    open:     "bg-blue-100 text-blue-700",
    pending:  "bg-amber-100 text-amber-700",
    canceled: "bg-slate-200 text-slate-600",
    expired:  "bg-slate-200 text-slate-600",
    failed:   "bg-red-100 text-red-700",
    test:     "bg-fuchsia-100 text-fuchsia-700",
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${clsMap[status] || "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

function MollieHealthWidget({ health, loading, onRefresh, onTest, testing, testResult, isDark }) {
  const reach = REACH_META[health?.reachability || "unknown"];
  const ReachIcon = reach.Icon;

  return (
    <section
      data-testid="mollie-health-widget"
      className={`rounded-xl border p-4 ${
        isDark ? "bg-slate-950 border-slate-800" : "bg-white border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Activity className={`w-4 h-4 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            Webhook Health
          </h3>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onRefresh}
            disabled={loading}
            data-testid="mollie-health-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button
            size="sm"
            onClick={onTest}
            disabled={testing}
            data-testid="mollie-health-test-webhook"
            className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
          >
            {testing ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin mr-1" /> Тест…
              </>
            ) : (
              <>
                <Send className="w-3 h-3 mr-1" /> Test webhook
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Reachability + timestamps */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div
          className={`rounded-lg border px-3 py-2 flex items-center gap-2 ${reach.cls}`}
          data-testid={`mollie-health-reachability-${health?.reachability || "unknown"}`}
        >
          <span className={`w-2 h-2 rounded-full ${reach.dot}`} />
          <ReachIcon className="w-4 h-4" />
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-70">Статус</div>
            <div className="text-xs font-bold">{reach.label}</div>
          </div>
        </div>
        <div className={`rounded-lg border px-3 py-2 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Последно събитие</div>
          <div className={`text-xs font-semibold ${isDark ? "text-white" : "text-slate-900"}`} data-testid="mollie-health-last-event">
            {fmtTs(health?.last_event_at)}
          </div>
        </div>
        <div className={`rounded-lg border px-3 py-2 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Последно платено</div>
          <div
            className={`text-xs font-semibold ${
              health?.last_paid_at ? "text-emerald-600" : (isDark ? "text-slate-400" : "text-slate-500")
            }`}
            data-testid="mollie-health-last-paid"
          >
            {health?.last_paid_at ? fmtTs(health.last_paid_at) : "Още няма"}
          </div>
        </div>
        <div className={`rounded-lg border px-3 py-2 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Броене (24ч / общо)</div>
          <div className={`text-xs font-semibold ${isDark ? "text-white" : "text-slate-900"}`} data-testid="mollie-health-counts">
            {health?.events_24h ?? 0} / {health?.events_total ?? 0} събития · {health?.paid_total ?? 0} paid
          </div>
        </div>
      </div>

      {/* Webhook URL (readable, verifiable) */}
      {health?.webhook_url && (
        <div className="mt-3 text-[10px] text-slate-500 flex items-center gap-1 font-mono break-all">
          <span className="uppercase tracking-wider text-slate-400">URL:</span>
          <span data-testid="mollie-health-webhook-url">{health.webhook_url}</span>
        </div>
      )}

      {/* Test-webhook result strip */}
      {testResult && (
        <div
          data-testid="mollie-health-test-result"
          className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
            testResult.reachable
              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
              : "border-red-300 bg-red-50 text-red-800"
          }`}
        >
          <div className="font-semibold">
            {testResult.reachable ? "✓ Webhook достижим" : "✗ Webhook НЕ е достижим"}
            {testResult.response_status ? ` — HTTP ${testResult.response_status}` : ""}
          </div>
          <div className="text-[10px] mt-0.5 opacity-80">
            tx: <span className="font-mono">{testResult.target_tx_id}</span> ·
            payment_id: <span className="font-mono">{testResult.target_payment_id}</span>
            {testResult.error && <> · err: {testResult.error}</>}
          </div>
        </div>
      )}

      {/* Recent events timeline */}
      <div className="mt-3">
        <div className={`text-[10px] uppercase tracking-wider mb-1.5 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          Последни {(health?.recent_events || []).length} събития
        </div>
        {(health?.recent_events || []).length === 0 ? (
          <div className={`text-xs italic ${isDark ? "text-slate-500" : "text-slate-400"}`}>
            Няма събития още — очаквайте първото плащане.
          </div>
        ) : (
          <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
            {(health.recent_events || []).map((ev) => (
              <div
                key={ev._id}
                data-testid={`mollie-event-${ev._id}`}
                className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${
                  isDark ? "bg-slate-900" : "bg-slate-50"
                }`}
              >
                <EventStatusBadge status={ev.status} />
                {ev.source === "admin-test" && (
                  <span className="text-[9px] font-bold text-fuchsia-600">TEST</span>
                )}
                <span className={`font-mono truncate flex-1 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  {ev.payment_id}
                </span>
                <span className={`whitespace-nowrap ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                  {fmtTs(ev.received_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

